import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiGet } from './apiUtils';
import { getCurrentUser } from './authUtils';
import { Button } from './components/ui/button';
import { AlertCircle, FileText, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './components/ui/alert';
import DateRangeSelector from './components/DateRangeSelector';
import { useDateRangeSelector } from './hooks/useDateRangeSelector';
import { generateSalesReportPDF } from './utils/pdfGenerators';

function SalesReport() {
  // Get shop name from user
  const user = getCurrentUser();
  const shopName = user.shopName || 'Easy Sheets, Daily';
  // Date range selector hook
  const dateSelector = useDateRangeSelector();
  
  // Extract values for use in dependencies
  const { reportType, selectedDate, selectedYear, selectedMonth, selectedWeek, customStartDate, customEndDate } = dateSelector;

  // Data
  const [rows, setRows] = useState([]);
  const [brandMap, setBrandMap] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [periodTotals, setPeriodTotals] = useState([]);
  const [hasAnyPriceDifference, setHasAnyPriceDifference] = useState(false);

  // Load master brands for MRP, packQuantity, kind
  useEffect(() => {
    const loadMasterBrands = async () => {
      try {
        const response = await apiGet('/api/master-brands');
        const list = await response.json();
        const m = new Map();
        (list || []).forEach(b => {
          m.set(b.id, {
            id: b.id,
            brandNumber: b.brandNumber,
            brandName: b.name,
            size: b.size,
            sizeCode: b.sizeCode,
            packType: b.packType,
            packQuantity: b.packQuantity,
            mrp: b.mrp,
            brandKind: b.brandKind || null
          });
        });
        setBrandMap(m);
      } catch (_) {}
    };
    loadMasterBrands();
  }, []);


  // Brand base name (group header)
  const getBaseName = (productName) => {
    return (productName || '').replace(/\s+(90ml|180ml|375ml|750ml|1000ml|2000ml|60ml|500ml|650ml|330ml|275ml).*$/i, '').trim();
  };

  // Fetch aggregated sales from backend (uses dsr.sales)
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!brandMap || brandMap.size === 0) {
        setLoading(false);
        return;
      }
      const { startDate, endDate } = dateSelector.getDateRange();
      // Determine display order from start date shop products
      const firstDayResponse = await apiGet(`/api/shop/products?date=${startDate}`);
      const firstDayJson = await firstDayResponse.json();
      const orderIndex = new Map();
      (firstDayJson.products || []).forEach((p, i) => orderIndex.set(`${p.brandNumber}|${p.sizeCode}`, i));

      const salesResponse = await apiGet(`/api/reports/sales?startDate=${startDate}&endDate=${endDate}`);
      const data = await salesResponse.json();

      const list = (data.rows || []).map(r => {
        const mb = brandMap.get(r.master_brand_id);
        const brandNumber = r.brand_number || mb?.brandNumber;
        const sizeCode = r.size_code || mb?.sizeCode;
        const brandName = r.brand_name || mb?.brandName || '';
        const key = `${brandNumber}|${sizeCode}`;
        const standardMrp = r.standard_mrp || mb?.mrp || 0;
        const actualPrice = r.actual_price !== null && r.actual_price !== undefined ? parseFloat(r.actual_price) : standardMrp;
        return {
          id: r.master_brand_id,
          key,
          order: orderIndex.has(key) ? orderIndex.get(key) : 9999,
          brandNumber,
          brandName,
          baseName: getBaseName(brandName),
          sizeCode,
          size: r.size_ml || mb?.size,
          packType: mb?.packType,
          packQuantity: r.pack_quantity || mb?.packQuantity,
          brandKind: r.brand_kind || mb?.brandKind || 'Other',
          mrp: standardMrp,
          actualPrice: actualPrice,
          hasPriceDifference: Math.abs(actualPrice - standardMrp) > 0.01,
          soldBottles: parseInt(r.sold_bottles, 10) || 0
        };
      });
      // Sort by initial view order, then size descending (numeric)
      list.sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        const as = parseInt(a.size, 10) || 0;
        const bs = parseInt(b.size, 10) || 0;
        return bs - as;
      });

      setRows(list);
      
      // Check if any products have price differences
      const hasDifferences = list.some(r => r.hasPriceDifference);
      setHasAnyPriceDifference(hasDifferences);

      // Build period totals (day-wise or month-wise)
      if (reportType === 'yearly') {
        const months = Array.from({ length: 12 }, (_, i) => i + 1);
        const ranges = months.map(m => ({
          start: new Date(selectedYear, m - 1, 1).toLocaleDateString('en-CA'),
          end: new Date(selectedYear, m, 0).toLocaleDateString('en-CA'),
          label: new Date(selectedYear, m - 1).toLocaleDateString('en-US', { month: 'long' })
        }));
        const results = await Promise.all(ranges.map(async r => {
          try {
            const response = await apiGet(`/api/reports/sales?startDate=${r.start}&endDate=${r.end}`);
            return await response.json();
          } catch (error) {
            return { rows: [] };
          }
        }));
        const totals = results.map((resp, idx) => {
          const mrpSum = (resp.rows || []).reduce((s, rr) => {
            return s + ((parseInt(rr.sold_bottles, 10) || 0) * (parseFloat(rr.standard_mrp) || 0));
          }, 0);
          const actualSum = (resp.rows || []).reduce((s, rr) => {
            const actualPrice = rr.actual_price !== null && rr.actual_price !== undefined ? parseFloat(rr.actual_price) : parseFloat(rr.standard_mrp);
            return s + ((parseInt(rr.sold_bottles, 10) || 0) * actualPrice);
          }, 0);
          return { label: ranges[idx].label, mrpTotal: mrpSum, actualTotal: actualSum };
        });
        setPeriodTotals(totals);
      } else {
        const dates = [];
        const c = new Date(startDate);
        const e = new Date(endDate);
        while (c <= e) { dates.push(c.toLocaleDateString('en-CA')); c.setDate(c.getDate() + 1); }
        const results = await Promise.all(dates.map(async d => {
          try {
            const response = await apiGet(`/api/reports/sales?startDate=${d}&endDate=${d}`);
            return await response.json();
          } catch (error) {
            return { rows: [] };
          }
        }));
        const totals = results.map((resp, idx) => {
          const mrpSum = (resp.rows || []).reduce((s, rr) => {
            return s + ((parseInt(rr.sold_bottles, 10) || 0) * (parseFloat(rr.standard_mrp) || 0));
          }, 0);
          const actualSum = (resp.rows || []).reduce((s, rr) => {
            const actualPrice = rr.actual_price !== null && rr.actual_price !== undefined ? parseFloat(rr.actual_price) : parseFloat(rr.standard_mrp);
            return s + ((parseInt(rr.sold_bottles, 10) || 0) * actualPrice);
          }, 0);
          const label = new Date(dates[idx]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
          return { label, mrpTotal: mrpSum, actualTotal: actualSum };
        });
        setPeriodTotals(totals);
      }
    } catch (e) {
      setError(e.message || 'Failed to load data');
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandMap, reportType, selectedDate, selectedYear, selectedMonth, selectedWeek, customStartDate, customEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totals = useMemo(() => {
    const totalBottles = rows.reduce((s, r) => s + (r.soldBottles || 0), 0);
    const totalMrp = rows.reduce((s, r) => s + (r.soldBottles * (r.mrp || 0)), 0);
    const totalActual = rows.reduce((s, r) => s + (r.soldBottles * (r.actualPrice || 0)), 0);
    return { totalBottles, totalMrp, totalActual };
  }, [rows]);

  // Kind totals for share table
  const kindTotals = useMemo(() => {
    const ORDER = ['Whisky', 'Beer', 'Brandy', 'Wine', 'Vodka', 'Rum', 'Gin', 'Liqueur', 'Tequila', 'Spirit', 'Ready to drink'];
    const canonical = (rawKind) => {
      switch ((rawKind || '').toString().trim().toUpperCase()) {
        case 'WHISKY':
        case 'WHISKEY':
          return 'whisky';
        case 'BEER':
          return 'beer';
        case 'BRANDY':
          return 'brandy';
        case 'WINE':
          return 'wine';
        case 'VODKA':
          return 'vodka';
        case 'RUM':
          return 'rum';
        case 'GIN':
          return 'gin';
        case 'LIQUEUR':
        case 'LIQUOR':
          return 'liqueur';
        case 'TEQUILA':
          return 'tequila';
        case 'SPIRIT':
          return 'spirit';
        case 'READY TO DRINK':
          return 'ready to drink';
        default:
          return 'other';
      }
    };
    const map = new Map();
    rows.forEach(r => {
      const key = canonical(r.brandKind);
      const mrpVal = (r.soldBottles || 0) * (r.mrp || 0);
      const actualVal = (r.soldBottles || 0) * (r.actualPrice || 0);
      const prev = map.get(key) || { mrp: 0, actual: 0 };
      prev.mrp += mrpVal;
      prev.actual += actualVal;
      map.set(key, prev);
    });
    const totalMrp = Array.from(map.values()).reduce((s, v) => s + (v.mrp || 0), 0) || 1;
    const totalActual = Array.from(map.values()).reduce((s, v) => s + (v.actual || 0), 0) || 1;
    return ORDER.map(label => {
      const key = canonical(label);
      const agg = map.get(key) || { mrp: 0, actual: 0 };
      const shareMrp = ((agg.mrp || 0) * 100) / totalMrp;
      const shareActual = ((agg.actual || 0) * 100) / totalActual;
      return { label, valueMrp: agg.mrp || 0, valueActual: agg.actual || 0, shareMrp, shareActual };
    });
  }, [rows]);

  const generatePDF = () => {
    const { startDate, endDate } = dateSelector.getDateRange();
    
    generateSalesReportPDF({
      rows,
      totals,
      kindTotals,
      periodTotals,
      startDate,
      endDate,
      reportType: dateSelector.reportType,
      selectedYear: dateSelector.selectedYear,
      selectedMonth: dateSelector.selectedMonth,
      shopName,
      hasAnyPriceDifference
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading sales report...</span>
        </div>
      </div>
    );
  }

  return (
      <main className="w-full px-4 py-8 space-y-6 max-w-7xl mx-auto">
        
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-left">Sales Report</h1>
          <p className="text-muted-foreground text-left">Per brand-size under brand, cases & bottles, MRP only</p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center justify-between">
                <span>{error}</span>
                <Button variant="outline" size="sm" onClick={fetchData}>Retry</Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <DateRangeSelector
          reportType={dateSelector.reportType}
          onReportTypeChange={dateSelector.setReportType}
          selectedDateObj={dateSelector.selectedDateObj}
          onDateSelect={dateSelector.handleDateSelect}
          selectedYear={dateSelector.selectedYear}
          onYearChange={dateSelector.handleYearChange}
          selectedMonth={dateSelector.selectedMonth}
          onMonthChange={dateSelector.handleMonthChange}
          selectedWeek={dateSelector.selectedWeek}
          onWeekChange={dateSelector.handleWeekChange}
          weeksInMonth={dateSelector.weeksInMonth}
          customStartDateObj={dateSelector.customStartDateObj}
          customEndDateObj={dateSelector.customEndDateObj}
          onCustomRangeSelect={dateSelector.handleCustomRangeSelect}
          onGenerateReport={fetchData}
          loading={loading}
          getMaxDate={dateSelector.getCurrentBusinessDateObject}
          hideGenerateButton={true}
        />

        <div className="flex justify-center">
          <Button onClick={generatePDF} size="lg" className="gap-2">
            <FileText className="h-5 w-5" />
            Generate PDF Report
          </Button>
        </div>
    </main>
  );
}

export default SalesReport;
